import * as React from 'react';
import {
	UPDATE_RANGE_DATA,
	UPDATE_INVEST_RETURN_DATA,
	UPDATE_MAX_DECLINE_DATA,
	UPDATE_STOCK_MARKET_DATA,
	UPDATE_AGE_RANGE_DATA,
	UPDATE_PLANNED_DATA,
} from './risk.constant';
import { riskContextReducer, RiskContextState, initialState } from './risk.context.reducer';
import { TRiskProfileResponse, TSidebarText } from '../../models/risk-profile.model';
import { useLead } from '../lead/lead.context';
import { TRiskQuestion } from '../../models/risk-question.model';

import {
	getAgeRangeText,
	getInvestmentText,
	getMaximumDeclineText,
	getPlannedWithdrawalText,
	getQuestionsDataBySequesnce,
	getStockMarketText,
} from '../../helpers/risk.helper';
import { LocalStorageUtils } from '@assetmark/ui-kit/dist/utils/local-storage.utils';
import { ABOUT_YOU_KEY, LEAD_APP_STATE } from '../../constants/storage.constant';
import { LeadProfileResponse } from '../../lib/das-api/model/leadProfileResponse';
import { LeadStrategyInfoResponse } from '../../lib/das-api/model/leadStrategyInfoResponse';
import { INVESTMENT_RETURN_RANGE_QUESTION } from '../../constants/data.constant';

type RiskProviderProps = { children: React.ReactNode };

const RiskStateContext = React.createContext<RiskContextState>(initialState);
RiskStateContext.displayName = 'RiskContext';

function RiskContextProvider({ children }: RiskProviderProps) {
	const [state, dispatch] = React.useReducer(riskContextReducer, initialState);
	const { leadProfile } = useLead();
	const leadState = LocalStorageUtils.getItem<LeadStrategyInfoResponse>(LEAD_APP_STATE);
	let investmentAmount = 0;
	if(leadState.isSome()) {
		const about = leadState.getOrElse({});
		investmentAmount = about.investmentAmount ? about.investmentAmount : 0;
	}

	const updateRiskData = (data: Array<TRiskQuestion>) => {
		// Investment return Range
		const investPayload =  getQuestionsDataBySequesnce('Investment Return Range', 1, data);
		const investText =  getInvestmentText(
			investPayload,
			state.assessData.invesetmentRangeData,
			investmentAmount,
		);
		
		dispatch({
			type: UPDATE_INVEST_RETURN_DATA,
			payload: { ...investPayload, question: INVESTMENT_RETURN_RANGE_QUESTION },
			text: investText,
		});
		// Maximum Decline
		const maxPayload =  getQuestionsDataBySequesnce('Maximum Decline', 2, data);
		const maxText =  getMaximumDeclineText(maxPayload, investmentAmount);
		dispatch({
			type: UPDATE_MAX_DECLINE_DATA,
			payload: maxPayload,
			text: maxText,
		});

		// Stock Market Forecast
		const stockPayload =  getQuestionsDataBySequesnce('Stock Market Forecast', 3, data);
		const stockText = getStockMarketText(stockPayload);
		dispatch({
			type: UPDATE_STOCK_MARKET_DATA,
			payload: stockPayload,
			text: stockText,
		});
		// Your Age Range
		const agePayload =  getQuestionsDataBySequesnce('Your Age Range', 4, data);
		const ageText = getAgeRangeText(agePayload);
		dispatch({
			type: UPDATE_AGE_RANGE_DATA,
			payload: agePayload,
			text: ageText,
		});
		// Planned Withdrawal
		const plannedPayload =  getQuestionsDataBySequesnce('Planned Withdrawal', 5, data);
		const plannedText = getPlannedWithdrawalText(plannedPayload);
		dispatch({
			type: UPDATE_PLANNED_DATA,
			payload: plannedPayload,
			text: plannedText,
		});
	};

	const updateRiskSidebarTextOnClick = (actionType: any, actionPayload: TSidebarText) => {
		dispatch({
			type: actionType,
			payload: {
				investmentText: actionPayload.investmentText,
				maxDeclineText: actionPayload.maxDeclineText,
				stockMarketText: actionPayload.stockMarketText,
				ageRangeText: actionPayload.ageRangeText,
				withdrawalText: actionPayload.withdrawalText,
			},
		});
	};

	const updateInvestmentRangeData = (riskRangeData: Array<TRiskProfileResponse>) => {
		const data = riskRangeData.map(r => ({
			id: r.id || 0,
			gainLoss: r.gainLoss
				? {
						gainPercent: r.gainLoss.gainPercent || 0,
						longTermAveragePercent: r.gainLoss.longTermAveragePercent || 0,
						lossPercent: r.gainLoss.lossPercent || 0,
				  }
				: { gainPercent: 0, longTermAveragePercent: 0, lossPercent: 0 },
		}));
		dispatch({ type: UPDATE_RANGE_DATA, payload: data });
	};

	//console.log('Risk State :>> ', state);
	const values = React.useMemo(
		() => ({ ...state, updateRiskData, updateInvestmentRangeData, updateRiskSidebarTextOnClick }),
		[state],
	);

	return <RiskStateContext.Provider value={{ ...values }}>{children}</RiskStateContext.Provider>;
}

function useRisk() {
	const context = React.useContext(RiskStateContext);
	if (context === undefined) {
		throw new Error('useRisk must be used within a RiskContextprovider');
	}
	return context;
}

export { RiskContextProvider, useRisk };
